import type { FormikValues } from 'formik'

import { getIn } from 'formik'
import i18n from 'i18next'

import type {
	QuestionDefinition,
	StrongValidationConstraint,
	ValidationConstraint,
} from '../types'

import { activeSubquestions } from '../model/questionnaireConfig/utils'
import { getValidationConstraintValue } from '../types'

export const validateAgainstQuestions = (
	values: FormikValues,
	questions: QuestionDefinition[],
): Record<string, unknown> => {
	const errors = {}

	questions.forEach((question) => {
		validateAgainstQuestion(values, question, errors)
	})

	return errors
}

export const validateAgainstQuestion = (
	values: FormikValues | undefined,
	question: QuestionDefinition,
	errors: Record<string, unknown>,
): void => {
	const { formFieldKey } = question

	const questionValue = getIn(values, formFieldKey)

	const validationError = validateValue(
		questionValue,
		extractStrongValidations(question),
	)

	if (validationError) {
		errors[formFieldKey] = validationError
	}

	const subQuestions = activeSubquestions(values, question)

	subQuestions?.forEach((subQuestion) => {
		validateAgainstQuestion(values, subQuestion, errors)
	})
}

const validateValue = (value: any, constraints: StrongValidations) => {
	if (constraints.required?.value && isValueEmpty(value)) {
		return i18n.t('required')
	}
	if (typeof value === 'string' && constraints.minLength !== undefined) {
		if (value.length < constraints.minLength.value) {
			return i18n.t('input-validation-minLength', {
				minLength: constraints.minLength.value,
			})
		}
	}
	if (
		typeof value === 'number' &&
		constraints.min !== undefined &&
		value < constraints.min.value
	) {
		return i18n.t('input-validation-minValue', {
			minValue: constraints.min.value,
		})
	}
	if (
		typeof value === 'number' &&
		constraints.max !== undefined &&
		value > constraints.max.value
	) {
		return i18n.t('input-validation-maxValue', {
			maxValue: constraints.max.value,
		})
	}
}

interface StrongValidations {
	required?: StrongValidationConstraint<boolean>
	minLength?: StrongValidationConstraint<number>
	min?: StrongValidationConstraint<number>
	max?: StrongValidationConstraint<number>
}

const extractStrongValidations = (
	question: QuestionDefinition,
): StrongValidations => {
	return {
		required: toStrongValidation(
			question.validations?.required,
			i18n.t('required'),
		),
		minLength: toStrongValidation(
			question.validations?.minLength,
			i18n.t('input-validation-minLength', {
				minLength: getValidationConstraintValue(
					question.validations?.minLength,
				),
			}),
		),
		min: toStrongValidation(
			question.validations?.min,
			i18n.t('input-validation-minValue', {
				minValue: getValidationConstraintValue(question.validations?.min),
			}),
		),
		max: toStrongValidation(
			question.validations?.max,
			i18n.t('input-validation-maxValue', {
				maxValue: getValidationConstraintValue(question.validations?.max),
			}),
		),
	}
}
const toStrongValidation = <RequirementValue>(
	validationConstraint: ValidationConstraint<RequirementValue> | undefined,
	defaultMessage: string,
): StrongValidationConstraint<RequirementValue> | undefined => {
	if (validationConstraint == undefined) {
		return undefined
	}
	if (
		typeof validationConstraint === 'object' &&
		'value' in validationConstraint
	) {
		return validationConstraint
	}
	return {
		value: validationConstraint,
		message: defaultMessage,
	}
}

export const isValueEmpty = (value: any): boolean => {
	return (
		value === null ||
		value === undefined ||
		(typeof value === 'string' && value.trim() === '') ||
		(Array.isArray(value) && value.length === 0) ||
		(typeof value === 'object' && isObjectEmpty(value))
	)
}

const isObjectEmpty = (obj: Record<string, unknown>): boolean => {
	for (const key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			return false
		}
	}

	return true
}
